import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ParallaxTower from './components/tower/ParallaxTower';
import AboutPage from './components/AboutPage';
import LegalPopup from './components/ui/LegalPopup';
import { PrivacyContent, TermsContent } from './data/legal';
import { ChevronLeft, ChevronRight, Briefcase } from 'lucide-react';

// New Badges
import AboutBadge from './components/badges/AboutBadge';
import ContactBadge from './components/badges/ContactBadge';
import FeaturedWorkBadge from './components/badges/FeaturedWorkBadge';
import PaymentBadge from './components/badges/PaymentBadge';
import PortfolioPage from './components/PortfolioPage';

// Portal
import CustomerPortal from './components/portal/CustomerPortal';

const App = () => {
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Future state for routing to portfolio
  const [currentView, setCurrentView] = useState('landing'); // 'landing' | 'portfolio' | 'about' | 'customer_portal'
  const [portalAuth, setPortalAuth] = useState({ id: null, name: null });

  // Embla setup
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    skipSnaps: false,
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Handle CTA routing
  const navigateToPortfolio = () => setCurrentView('portfolio');
  const navigateToAbout = () => setCurrentView('about');
  const navigateToLanding = () => setCurrentView('landing');
  const navigateToPortal = (id, name) => {
    setPortalAuth({ id, name });
    setCurrentView('customer_portal');
  };

  // Slides mapping
  const slides = [
    { id: 'hero', component: <div className="w-full h-[550px]" /> }, // Empty slot to view tower
    { id: 'about', component: <AboutBadge onSeePortfolio={navigateToPortfolio} /> },
    { id: 'work', component: <FeaturedWorkBadge onSeePortfolio={navigateToPortfolio} /> },
    { id: 'payment', component: <PaymentBadge onAuthenticated={navigateToPortal} /> },
    { id: 'contact', component: <ContactBadge /> }
  ];

  // If we are in about view, render the full About page
  if (currentView === 'about') {
    return (
      <AboutPage
        onBack={navigateToLanding}
        onSeePortfolio={navigateToPortfolio}
        onContact={() => {
          navigateToLanding();
          // Brief timeout to let landing mount, then jump to contact slide
          setTimeout(() => emblaApi?.scrollTo(4), 80);
        }}
      />
    );
  }

  // If we are in portfolio view, render that instead of the Carousel Landing Page
  if (currentView === 'portfolio') {
    return (
      <PortfolioPage
        onBack={navigateToLanding}
      />
    );
  }

  // If we are in customer portal view
  if (currentView === 'customer_portal') {
    return (
      <CustomerPortal 
        customerId={portalAuth.id} 
        customerName={portalAuth.name}
        onBack={navigateToLanding} 
      />
    );
  }

  // Normal Landing Page Render
  return (
    <>
      <div className="relative overflow-hidden min-h-screen bg-[#023666]">
        {/* Parallax Background - Now driven by Carousel Index */}
        <ParallaxTower activeIndex={activeIndex} />

        {/* Fixed Header */}
        <div className="relative" style={{ zIndex: 20 }}>
          <Header
            activeIndex={activeIndex}
            onNavigate={(index) => emblaApi?.scrollTo(index)}
            onNavigateToAbout={navigateToAbout}
          />
        </div>

        {/* Main Content - Full Screen Carousel */}
        <main className="relative z-10 h-screen w-full flex flex-col items-center justify-center pointer-events-none">
          
          {/* Embla Carousel Container */}
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-8 relative pointer-events-auto">
            
            {/* Viewport */}
            <div className="overflow-hidden bg-gradient-to-b from-white/[0.05] to-transparent backdrop-blur-[2px] rounded-[3rem] border border-white/5 py-12" ref={emblaRef}>
              <div className="flex touch-pan-y">
                {slides.map((slide, index) => {
                  const isActive = index === activeIndex;
                  return (
                    <div 
                      key={slide.id} 
                      className="flex-[0_0_100%] min-w-0 md:flex-[0_0_60%] lg:flex-[0_0_45%] px-4 transition-all duration-[600ms] ease-out flex items-center justify-center"
                      style={{
                        opacity: isActive ? 1 : 0.4,
                        transform: `scale(${isActive ? 1 : 0.85}) rotateY(${isActive ? 0 : (index > activeIndex ? -15 : 15)}deg)`,
                        perspective: '1000px',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      {/* The Badge Container scales up to fit container */}
                      <div className="w-full h-full py-4 pointer-events-auto flex items-center justify-center">
                        {slide.component}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={scrollPrev}
              className="absolute left-4 md:left-2 lg:-left-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#1a2b4c]/80 backdrop-blur-xl border border-white/20 text-white flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all z-20 pointer-events-auto shadow-[0_0_30px_rgba(34,211,238,0.2)]"
            >
              <ChevronLeft className="w-8 h-8 -ml-1" />
            </button>

            <button
              onClick={scrollNext}
              className="absolute right-4 md:right-2 lg:-right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#1a2b4c]/80 backdrop-blur-xl border border-white/20 text-white flex items-center justify-center hover:bg-white/20 hover:scale-110 transition-all z-20 pointer-events-auto shadow-[0_0_30px_rgba(59,130,246,0.2)]"
            >
              <ChevronRight className="w-8 h-8 -mr-1" />
            </button>

            {/* Dots Indicator */}
            <div className="absolute -bottom-10 left-0 right-0 flex justify-center gap-3 pointer-events-auto">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => emblaApi?.scrollTo(index)}
                  className={`h-2.5 rounded-full transition-all duration-500 ease-out ${
                    index === activeIndex 
                      ? 'w-10 bg-gradient-to-r from-cyan-400 to-blue-500 shadow-[0_0_10px_rgba(34,211,238,0.5)]' 
                      : 'w-2.5 bg-white/20 hover:bg-white/40'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

          </div>
        </main>
        
        {/* Fixed Footer */}
        <div className="relative z-20 pointer-events-auto">
          <Footer
            onShowPrivacy={() => setShowPrivacyPopup(true)}
            onShowTerms={() => setShowTermsPopup(true)}
            onNavigateToAbout={navigateToAbout}
          />
        </div>
      </div>

      <div className="pointer-events-auto">
        <LegalPopup
          isOpen={showPrivacyPopup}
          onClose={() => setShowPrivacyPopup(false)}
          title="Privacy Policy"
          content={<PrivacyContent />} 
        />

        <LegalPopup
          isOpen={showTermsPopup}
          onClose={() => setShowTermsPopup(false)}
          title="Terms of Service"
          content={<TermsContent />}
        />
      </div>
    </>
  );
};

export default App;
